import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Divider,
  TextField,
  InputAdornment,
  Button,
  ButtonBase,
  Chip,
  Collapse,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import BlockIcon from "@mui/icons-material/Block";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import PersonIcon from "@mui/icons-material/Person";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockClockIcon from "@mui/icons-material/LockClock";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

import {
  useWhatsappchatlistQuery,
  useWhatappchatmessagesQuery,
  useGetWhatsappChatAgentsQuery,
  useAssignAgentToChatMutation,
  useSendMessageMutation,
  useGetTemplatesQuery,
  useGetPhoneNUmberQuery,
} from "../store/services/whatsappApi";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { layoutCSS } from "../component/common/commonStyle";

/* ───────────────────────── helpers ───────────────────────── */

const DAY_MS = 24 * 60 * 60 * 1000;
const EMPTY_READ = { phones: {}, baseline: {}, allAt: 0 };
const EASE = "cubic-bezier(.4,0,.2,1)";
const BRAND = "#6366f1";
const BRAND_DARK = "#4f46e5";
const INK = "#0f172a";
const LINE = "#e8ecf3";

const ts = (m) => new Date(m?.timestamp).getTime() || 0;
const chatKey = (campaignId, number) => `${campaignId}|${number}`;
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" });
const preview = (m) => m?.text || (m?.type ? `[${m.type}]` : "");
const lastActivity = (c) =>
  new Date(c.last_message_at || c.last_inbound_at || c.updated_at || c.created_at || 0).getTime() || 0;

const formatPhone = (n) => {
  const d = String(n || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) return `+91 ${d.slice(2, 7)} ${d.slice(7)}`;
  if (d.length > 10) return `+${d}`;
  return String(n || "");
};

const shortTime = (t) => {
  if (!t) return "";
  const d = new Date(t);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return fmtTime(d);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const formatDateLabel = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
};

const hueOf = (seed) => {
  let h = 0;
  String(seed).split("").forEach((ch) => { h = (h * 31 + ch.charCodeAt(0)) % 360; });
  return h;
};

/* ── template helpers (Initiate chat) ── */

// highest {{n}} found in a piece of text (0 if none)
const maxPlaceholder = (text) => {
  const nums = (String(text || "").match(/{{\s*(\d+)\s*}}/g) || []).map((m) => parseInt(m.match(/\d+/)[0], 10));
  return nums.length ? Math.max(...nums) : 0;
};

// which {{n}} inputs a template needs: header text, body, URL buttons
const getTemplateFields = (tpl) => {
  const fields = [];
  (tpl?.components || []).forEach((c) => {
    if (c.type === "HEADER" && c.format === "TEXT") {
      for (let i = 1; i <= maxPlaceholder(c.text); i++)
        fields.push({ key: `header-${i}`, section: "header", label: `Header {{${i}}}` });
    }
    if (c.type === "BODY") {
      for (let i = 1; i <= maxPlaceholder(c.text); i++)
        fields.push({ key: `body-${i}`, section: "body", label: `Body {{${i}}}` });
    }
    if (c.type === "BUTTONS") {
      (c.buttons || []).forEach((b, bi) => {
        if (b.type === "URL") {
          for (let i = 1; i <= maxPlaceholder(b.url); i++)
            fields.push({ key: `btn${bi}-${i}`, section: "button", btnIndex: bi, label: `Button "${b.text}" {{${i}}}` });
        }
      });
    }
  });
  return fields;
};

const buildTemplateComponents = (fields, values) => {
  const comps = [];
  const header = fields.filter((f) => f.section === "header");
  const body = fields.filter((f) => f.section === "body");
  if (header.length) comps.push({ type: "header", parameters: header.map((f) => ({ type: "text", text: values[f.key] || "" })) });
  if (body.length) comps.push({ type: "body", parameters: body.map((f) => ({ type: "text", text: values[f.key] || "" })) });
  [...new Set(fields.filter((f) => f.section === "button").map((f) => f.btnIndex))].forEach((bi) => {
    comps.push({
      type: "button",
      sub_type: "url",
      index: String(bi),
      parameters: fields
        .filter((f) => f.section === "button" && f.btnIndex === bi)
        .map((f) => ({ type: "text", text: values[f.key] || "" })),
    });
  });
  return comps;
};

// body text with the typed values filled in (used for preview + the optimistic chat bubble)
const renderBody = (tpl, fields, values) => {
  let text = (tpl?.components || []).find((c) => c.type === "BODY")?.text || "";
  fields.filter((f) => f.section === "body").forEach((f, i) => {
    text = text.replace(`{{${i + 1}}}`, values[f.key] || `{{${i + 1}}}`);
  });
  return text;
};

/* ───────────────────────── small UI pieces ───────────────────────── */

const UnreadPill = ({ n }) =>
  n > 0 ? (
    <Box
      sx={{
        minWidth: 19, height: 19, px: 0.7, borderRadius: "10px", bgcolor: "#ef4444", color: "#fff",
        fontSize: "0.66rem", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "0 2px 6px rgba(239,68,68,.35)",
      }}
    >
      {n > 99 ? "99+" : n}
    </Box>
  ) : null;

const ContactAvatar = ({ seed, size = 38 }) => {
  const hue = hueOf(seed);
  return (
    <Avatar sx={{ width: size, height: size, bgcolor: `hsl(${hue} 80% 93%)`, color: `hsl(${hue} 55% 38%)`, flexShrink: 0 }}>
      <PersonIcon sx={{ fontSize: size * 0.56 }} />
    </Avatar>
  );
};

const ChatRow = ({ selected, onClick, avatar, title, subtitle, time, unread, indent }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      width: "100%", display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1.25, textAlign: "left",
      pr: 1.5, py: 1, pl: indent ? 4 : 1.5, position: "relative", borderRadius: "10px",
      bgcolor: selected ? "#eef2ff" : "transparent", transition: `background .15s ${EASE}`,
      "&:hover": { bgcolor: selected ? "#eef2ff" : "#f6f8fc" },
    }}
  >
    {selected && <Box sx={{ position: "absolute", left: 0, top: 9, bottom: 9, width: 3, borderRadius: 2, bgcolor: BRAND }} />}
    {avatar}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: "0.83rem", fontWeight: unread ? 700 : 600, color: INK }}>{title}</Typography>
      <Typography noWrap sx={{ fontSize: "0.72rem", color: unread ? "#475569" : "#94a3b8", fontWeight: unread ? 500 : 400 }}>
        {subtitle}
      </Typography>
    </Box>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5, flexShrink: 0 }}>
      {time ? <Typography sx={{ fontSize: "0.64rem", fontWeight: 600, color: unread ? BRAND : "#94a3b8" }}>{time}</Typography> : null}
      <UnreadPill n={unread} />
    </Box>
  </ButtonBase>
);

/* A slim tab that slides its content open from the top. Only one is open at a time. */
const SlideSection = ({ icon, label, hint, count, unread, open, onToggle, children }) => (
  <Box
    sx={{
      display: "flex", flexDirection: "column", minHeight: 0,
      flexGrow: open ? 1 : 0, flexShrink: 1, flexBasis: "auto",
      transition: `flex-grow .4s ${EASE}, box-shadow .3s ${EASE}`,
      bgcolor: "#fff", border: `1px solid ${LINE}`, borderRadius: "14px", overflow: "hidden",
      boxShadow: open ? "0 8px 24px rgba(79,70,229,.10)" : "0 1px 2px rgba(15,23,42,.04)",
    }}
  >
    <ButtonBase
      onClick={onToggle}
      aria-expanded={open}
      sx={{
        height: 46, px: 1.5, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1.1,
        bgcolor: open ? "#f5f6ff" : "#fff", transition: `background .2s ${EASE}`,
        "&:hover": { bgcolor: open ? "#f5f6ff" : "#fafbff" },
      }}
    >
      <Box sx={{ width: 28, height: 28, borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: open ? BRAND : "#eef0f7", color: open ? "#fff" : "#64748b", transition: `all .25s ${EASE}` }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Typography sx={{ fontSize: "0.84rem", fontWeight: 700, color: INK, lineHeight: 1.15 }}>{label}</Typography>
        <Typography sx={{ fontSize: "0.66rem", color: "#94a3b8", lineHeight: 1.2 }}>{hint}</Typography>
      </Box>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", bgcolor: "#eef0f7", borderRadius: "8px", px: 0.9, py: 0.15 }}>{count}</Typography>
      <UnreadPill n={unread} />
      <ExpandMoreIcon sx={{ fontSize: 20, color: "#94a3b8", transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: `transform .3s ${EASE}` }} />
    </ButtonBase>

    <Box
      sx={{
        flex: 1, minHeight: 0, position: "relative", overflow: "hidden",
        visibility: open ? "visible" : "hidden",
        transition: open ? "visibility 0s" : "visibility 0s linear .4s",
        borderTop: open ? `1px solid ${LINE}` : "1px solid transparent",
      }}
    >
      <Box
        sx={{
          position: "absolute", inset: 0, overflowY: "auto", p: 0.75,
          opacity: open ? 1 : 0, transform: open ? "translateY(0)" : "translateY(-14px)",
          transition: `opacity .3s ${EASE} ${open ? ".1s" : "0s"}, transform .4s ${EASE}`,
          "&::-webkit-scrollbar": { width: 5 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#dbe0ec", borderRadius: 3 },
        }}
      >
        {children}
      </Box>
    </Box>
  </Box>
);

const EmptyNote = ({ children }) => (
  <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
    <Typography sx={{ fontSize: "0.78rem", color: "#94a3b8" }}>{children}</Typography>
  </Box>
);

const InfoRow = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: "0.66rem", color: "#94a3b8", fontWeight: 600, mb: 0.4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</Typography>
    {children}
  </Box>
);

/* ───────────────────────── component ───────────────────────── */

export default function LiveChatDashboard() {
  const [customer_id, setCustomer_id] = useState(null);
  const [input, setInput] = useState("");
  const [search, setSearch] = useState("");
  const [openSection, setOpenSection] = useState(null); // null | "direct" | "campaigns"  (both closed by default)
  const [expandedId, setExpandedId] = useState(null); // campaign opened inside "Campaigns"
  const [selectedChat, setSelectedChat] = useState(null); // { campaign_id, number }
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [showDetails, setShowDetails] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [searchParams, setSearchParams] = useSearchParams();
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 1, has_next: false, has_previous: false });
  const [campaignsList, setCampaignsList] = useState([]);
  const [pending, setPending] = useState([]); // messages we just sent, until the server returns them
  const [now, setNow] = useState(Date.now());

  // "Initiate chat" dialog (send an approved template when the 24h window is closed)
  const [templateOpen, setTemplateOpen] = useState(false);
  const [tplSearch, setTplSearch] = useState("");
  const [tplSelectedId, setTplSelectedId] = useState(null);
  const [tplValues, setTplValues] = useState({});

  // "read" state is saved in the browser so red dots do not come back after refresh:
  //   phones[chatKey]    = time of the newest customer message you have seen in that chat
  //   baseline[campaign] = inbound messages that were already read for that campaign
  //   allAt              = time of "Mark all as read"
  const [read, setRead] = useState(EMPTY_READ);
  const [readReady, setReadReady] = useState(false);

  const bottomRef = useRef(null);
  const restoredRef = useRef(false);

  const [sendMessage, { isLoading: isSendingMessage }] = useSendMessageMutation();

  /* ── clock tick (keeps the 24h session timer correct) ── */
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  /* ── auth ── */
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) { toast.error("Access token not found"); return; }
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid || null;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomer_id(id);
    } catch (err) {
      console.error("Error decoding token:", err);
      toast.error("Invalid access token format");
    }
  }, []);

  /* ── load / save read state ── */
  const storageKey = customer_id ? `livechat-read-${customer_id}` : null;

  useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = localStorage.getItem(storageKey);
      setRead(raw ? { ...EMPTY_READ, ...JSON.parse(raw) } : EMPTY_READ);
    } catch {
      setRead(EMPTY_READ);
    }
    setReadReady(true);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey || !readReady) return;
    try { localStorage.setItem(storageKey, JSON.stringify(read)); } catch { /* storage full / blocked */ }
  }, [read, storageKey, readReady]);

  /* ── campaigns / direct chats list ── */
  const {
    currentData: campaignsResponse,
    isLoading: isLoadingCampaigns,
    isFetching: isFetchingCampaigns,
    isError: isCampaignsError,
    refetch: refetchCampaigns,
  } = useWhatsappchatlistQuery(
    { customer_id, page: pagination.page, limit: pagination.limit },
    { pollingInterval: 15000, skip: !customer_id, refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!campaignsResponse) return;
    const respPage = campaignsResponse.pagination?.page ?? 1;
    const incoming = campaignsResponse.campaigns || [];
    setCampaignsList((prev) => {
      const map = new Map();
      (respPage === 1 ? [] : prev).forEach((c) => map.set(c.campaign_id, c));
      incoming.forEach((c) => map.set(c.campaign_id, c));
      return Array.from(map.values());
    });
    if (campaignsResponse.pagination) setPagination(campaignsResponse.pagination);
  }, [campaignsResponse]);

  useEffect(() => {
    if (customer_id) { setPagination((p) => ({ ...p, page: 1 })); setCampaignsList([]); }
  }, [customer_id]);

  const campaignById = useMemo(
    () => new Map(campaignsList.map((c) => [String(c.campaign_id), c])),
    [campaignsList]
  );

  const expandedCampaign = expandedId != null ? campaignById.get(String(expandedId)) : null;
  const chatCampaign = selectedChat ? campaignById.get(String(selectedChat.campaign_id)) : null;

  /* ── messages: one query for the open campaign's contact list, one for the open chat ── */
  const listQ = useWhatappchatmessagesQuery(
    { customer_id, campaign_id: expandedId },
    { skip: !customer_id || expandedId == null, pollingInterval: 10000, refetchOnMountOrArgChange: true }
  );
  const chatQ = useWhatappchatmessagesQuery(
    { customer_id, campaign_id: selectedChat?.campaign_id },
    { skip: !customer_id || !selectedChat, pollingInterval: 10000, refetchOnMountOrArgChange: true }
  );
  const listReplies = listQ.currentData?.replies ?? null;
  const chatReplies = chatQ.currentData?.replies ?? null;

  /* ── agents ── */
  const { data: agentsResponse } = useGetWhatsappChatAgentsQuery(customer_id, { skip: !customer_id });
  const [assignAgentToChat] = useAssignAgentToChatMutation();

  /* ── templates for "Initiate chat" (only loaded when the dialog is open) ── */
  const { data: phoneData } = useGetPhoneNUmberQuery(customer_id, { skip: !customer_id || !templateOpen });

  const chatWabaId = phoneData?.phone_numbers?.find(
    (p) => p.phone_number_id === chatCampaign?.phone_number_id
  )?.waba_id;

  const { data: templateData, isLoading: isLoadingTemplates } = useGetTemplatesQuery(
    { customer_id },
    { skip: !customer_id || !templateOpen || !chatWabaId }
  );

  const approvedTemplates = useMemo(
    () =>
      (templateData?.templates ?? [])
        .filter((t) => String(t.waba_id) === String(chatWabaId))
        .filter((t) => t.status?.toUpperCase() === "APPROVED")
        .filter((t) => t.name?.toLowerCase().includes(tplSearch.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [templateData, chatWabaId, tplSearch]
  );

  const selectedTpl = approvedTemplates.find((t) => t.template_id === tplSelectedId) || null;
  const tplFields = useMemo(() => getTemplateFields(selectedTpl), [selectedTpl]);
  const tplFilled = tplFields.every((f) => (tplValues[f.key] || "").trim());

  const closeTemplateDialog = () => {
    setTemplateOpen(false);
    setTplSearch("");
    setTplSelectedId(null);
    setTplValues({});
  };

  /* ── contacts of a campaign (with unread counts) ── */
  const buildContacts = useCallback((replies, campaignId) => {
    if (!replies) return null;
    return Object.entries(replies)
      .map(([number, r]) => {
        const inbound = r?.inbound || [];
        const outbound = r?.outbound || [];
        const newest = [...inbound, ...outbound].sort((a, b) => ts(b) - ts(a))[0];
        const seenT = Math.max(read.phones[chatKey(campaignId, number)] || 0, read.allAt || 0);
        return {
          number,
          inboundCount: inbound.length,
          unread: inbound.filter((m) => ts(m) > seenT).length,
          lastText: preview(newest),
          lastTs: newest ? ts(newest) : 0,
        };
      })
      .filter((c) => c.inboundCount > 0)
      .sort((a, b) => b.lastTs - a.lastTs);
  }, [read]);

  const listContacts = useMemo(
    () => (expandedId != null ? buildContacts(listReplies, expandedId) : null),
    [buildContacts, listReplies, expandedId]
  );
  const chatContacts = useMemo(
    () => (selectedChat ? buildContacts(chatReplies, selectedChat.campaign_id) : null),
    [buildContacts, chatReplies, selectedChat]
  );

  const sumUnread = (contacts) => contacts.reduce((s, c) => s + c.unread, 0);

  /* unread count for a campaign / direct chat */
  const campaignUnread = useCallback((c) => {
    const id = String(c.campaign_id);
    if (expandedId != null && String(expandedId) === id && listContacts) return sumUnread(listContacts);
    if (selectedChat && String(selectedChat.campaign_id) === id && chatContacts) return sumUnread(chatContacts);
    return Math.max(0, (c.inbound_count || 0) - (read.baseline[id] || 0));
  }, [expandedId, listContacts, selectedChat, chatContacts, read.baseline]);

  /* remember how many messages were already read whenever a campaign's contacts are known */
  const recordBaseline = useCallback((campaign, contacts) => {
    if (!campaign || !contacts) return;
    const id = String(campaign.campaign_id);
    const base = Math.max(0, (campaign.inbound_count || 0) - sumUnread(contacts));
    setRead((prev) => (prev.baseline[id] === base ? prev : { ...prev, baseline: { ...prev.baseline, [id]: base } }));
  }, []);

  useEffect(() => { recordBaseline(expandedCampaign, listContacts); }, [expandedCampaign, listContacts, recordBaseline]);
  useEffect(() => { recordBaseline(chatCampaign, chatContacts); }, [chatCampaign, chatContacts, recordBaseline]);

  /* opening a chat (and receiving new messages while it is open) marks it as read */
  const chatInbound = useMemo(
    () => (selectedChat && chatReplies ? chatReplies[selectedChat.number]?.inbound || [] : null),
    [selectedChat, chatReplies]
  );

  useEffect(() => {
    if (!selectedChat || !chatInbound || chatInbound.length === 0) return;
    const key = chatKey(selectedChat.campaign_id, selectedChat.number);
    const newest = Math.max(...chatInbound.map(ts));
    setRead((prev) => (
      (prev.phones[key] || 0) >= newest ? prev : { ...prev, phones: { ...prev.phones, [key]: newest } }
    ));
  }, [selectedChat, chatInbound]);

  /* ── sidebar data: direct messages and campaigns ── */
  const allDirect = useMemo(
    () => campaignsList
      .filter((c) => c.is_direct_message)
      .map((c) => {
        const main = c.related_campaign_id ? campaignById.get(String(c.related_campaign_id)) : null;
        return {
          ...c,
          number: c.sender_phone_number || "",
          title: formatPhone(c.sender_phone_number) || c.campaign_name || `Chat ${c.campaign_id}`,
          subtitle: c.related_campaign_id
            ? `Replied to ${main?.campaign_name || "a campaign"}`
            : "Direct chat",
          unread: campaignUnread(c),
        };
      })
      .sort((a, b) => lastActivity(b) - lastActivity(a)),
    [campaignsList, campaignById, campaignUnread]
  );

  const allCampaigns = useMemo(
    () => campaignsList
      .filter((c) => !c.is_direct_message)
      .map((c) => ({ ...c, unread: campaignUnread(c) }))
      .sort((a, b) => lastActivity(b) - lastActivity(a)),
    [campaignsList, campaignUnread]
  );

  const q = search.trim().toLowerCase();
  const directChats = q
    ? allDirect.filter((c) => c.title.toLowerCase().includes(q) || String(c.number).includes(q) || c.subtitle.toLowerCase().includes(q))
    : allDirect;
  const campaignRows = q
    ? allCampaigns.filter((c) => (c.campaign_name || "").toLowerCase().includes(q))
    : allCampaigns;

  const directUnread = directChats.reduce((s, c) => s + c.unread, 0);
  const campaignsUnread = campaignRows.reduce((s, c) => s + c.unread, 0);
  const totalUnread = allDirect.reduce((s, c) => s + c.unread, 0) + allCampaigns.reduce((s, c) => s + c.unread, 0);

  // while searching, open the section that has results
  useEffect(() => {
    if (!q) return;
    setOpenSection(directChats.length ? "direct" : campaignRows.length ? "campaigns" : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const toggleSection = (id) => setOpenSection((prev) => (prev === id ? null : id));
  const toggleCampaign = (id) => setExpandedId((prev) => (prev === id ? null : id));

  /* ── open chat + URL ── */
  const openChat = (campaign_id, number) => {
    setSelectedChat({ campaign_id, number });
    setSelectedAgentId("");
    setInput("");
    const p = new URLSearchParams(searchParams);
    p.set("campaignId", String(campaign_id));
    p.set("phone", number);
    p.delete("phoneId");
    setSearchParams(p, { replace: true });
  };

  // restore the open chat after a page refresh
  useEffect(() => {
    if (restoredRef.current || campaignsList.length === 0) return;
    restoredRef.current = true;
    const cid = searchParams.get("campaignId");
    const number = searchParams.get("phone");
    if (!cid || !number) return;
    const c = campaignById.get(cid);
    setSelectedChat({ campaign_id: c?.campaign_id ?? cid, number });
    if (c && !c.is_direct_message) { setExpandedId(c.campaign_id); setOpenSection("campaigns"); }
    else setOpenSection("direct");
  }, [campaignsList, campaignById, searchParams]);

  /* ── messages of the open chat ── */
  const currentKey = selectedChat ? chatKey(selectedChat.campaign_id, selectedChat.number) : null;

  const serverMessages = useMemo(() => {
    if (!selectedChat || !chatReplies) return [];
    const r = chatReplies[selectedChat.number] || {};
    return [
      ...(r.inbound || []).map((m) => ({ ...m, sender: "customer" })),
      ...(r.outbound || []).map((m) => ({ ...m, sender: "agent" })),
    ]
      .sort((a, b) => ts(a) - ts(b))
      .map((m) => ({
        id: m.message_id,
        text: preview(m),
        sender: m.sender,
        type: m.type,
        timestamp: m.timestamp,
        time: fmtTime(m.timestamp),
      }));
  }, [selectedChat, chatReplies]);

  const messages = useMemo(() => {
    const ids = new Set(serverMessages.map((m) => m.id));
    const extra = pending
      .filter((p) => p.chatKey === currentKey)
      .filter((p) => !(p.serverId && ids.has(p.serverId)))
      .filter((p) => !(p.status === "sent" && serverMessages.some(
        (m) => m.sender === "agent" && m.text === p.text && ts(m) >= p.ts - 120000
      )))
      .map((p) => ({
        id: p.id,
        text: p.text,
        sender: "agent",
        timestamp: new Date(p.ts).toISOString(),
        time: fmtTime(p.ts),
        isPending: p.status === "sending",
        error: p.status === "failed",
        errorMessage: p.errorMessage,
      }));
    return [...serverMessages, ...extra];
  }, [serverMessages, pending, currentKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length, currentKey]);

  /* ── 24 hour session ── */
  const lastInbound = useMemo(() => [...messages].reverse().find((m) => m.sender === "customer"), [messages]);
  const lastInboundMessageId = lastInbound?.id || null;
  const sessionLeft = lastInbound ? DAY_MS - (now - ts(lastInbound)) : null;
  const sessionExpired = sessionLeft !== null && sessionLeft <= 0;
  const sessionTimeRemaining = useMemo(() => {
    if (sessionLeft === null || sessionLeft <= 0) return null;
    const hrs = Math.floor(sessionLeft / 3600000);
    const mins = Math.floor((sessionLeft % 3600000) / 60000);
    return hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
  }, [sessionLeft]);

  /* ── actions ── */
  const handleAssignAgent = async (agentId) => {
    const agent = (agentsResponse || []).find((a) => a.agent_id === agentId);
    if (!agent || !selectedChat) return;
    if (!lastInboundMessageId) { toast.error("No customer message to assign an agent to"); return; }
    try {
      await assignAgentToChat({ customer_id, message_id: lastInboundMessageId, agent_id: agent.agent_id }).unwrap();
      setSelectedAgentId(agentId);
      toast.success("Agent assigned successfully");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to assign agent");
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selectedChat || !customer_id || isSendingMessage || sessionExpired) return;
    const phone_number_id = chatCampaign?.phone_number_id;
    if (!phone_number_id) { toast.error("Could not find the WhatsApp number for this chat"); return; }

    const id = `temp-${Date.now()}`;
    setPending((prev) => [...prev, { id, chatKey: currentKey, text, ts: Date.now(), status: "sending" }]);
    setInput("");

    try {
      const payload = {
        phone_number_id,
        recipient: selectedChat.number,
        message_type: "text",
        text_body: text,
        customer_id,
        campaign_id: selectedChat.campaign_id,
        campaign_name: chatCampaign?.campaign_name || `Campaign ${selectedChat.campaign_id}`,
        message_send_interval: "1",
      };
      if (lastInboundMessageId) payload.context_message_id = lastInboundMessageId;

      const response = await sendMessage({ payload, customer_id }).unwrap();
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: "sent", serverId: response?.message_id } : p)));
      setTimeout(() => { try { chatQ.refetch(); } catch { /* ignore */ } }, 1000);
    } catch (error) {
      const msg = error?.data?.message || error?.message || "Failed to send message.";
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: "failed", errorMessage: msg } : p)));
      setInput((cur) => cur || text); // give the text back so it can be edited and re-sent
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
  };

  /* send an approved template to this chat's number (re-opens the conversation) */
  const handleSendTemplate = async () => {
    if (!selectedTpl || !selectedChat || !customer_id) return;
    const phone_number_id = chatCampaign?.phone_number_id;
    if (!phone_number_id) { toast.error("Could not find the WhatsApp number for this chat"); return; }
    if (!tplFilled) { toast.error("Please fill all template values"); return; }

    const text = renderBody(selectedTpl, tplFields, tplValues) || `[Template] ${selectedTpl.name}`;
    const id = `temp-${Date.now()}`;

    try {
      const payload = {
        phone_number_id,
        recipient: selectedChat.number,
        message_type: "template",
        template_id: selectedTpl.template_id,
        template_components: buildTemplateComponents(tplFields, tplValues),
        customer_id,
        campaign_id: selectedChat.campaign_id,
        campaign_name: chatCampaign?.campaign_name || `Campaign ${selectedChat.campaign_id}`,
        message_send_interval: "1",
      };

      setPending((prev) => [...prev, { id, chatKey: currentKey, text, ts: Date.now(), status: "sending" }]);
      const res = await sendMessage({ payload, customer_id }).unwrap();
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: "sent", serverId: res?.message_id } : p)));
      toast.success("Template sent");
      closeTemplateDialog();
      setTimeout(() => { try { chatQ.refetch(); } catch { /* ignore */ } }, 1000);
    } catch (err) {
      const msg =
        err?.data?.error?.error_user_msg ||
        err?.data?.error?.message ||
        err?.data?.message ||
        err?.error ||
        "Failed to send template";
      setPending((prev) => prev.map((p) => (p.id === id ? { ...p, status: "failed", errorMessage: msg } : p)));
      toast.error(msg);
    }
  };

  const handleMarkAllAsRead = () => {
    setRead((prev) => {
      const baseline = { ...prev.baseline };
      campaignsList.forEach((c) => { baseline[String(c.campaign_id)] = Math.max(baseline[String(c.campaign_id)] || 0, c.inbound_count || 0); });
      return { ...prev, baseline, allAt: Date.now() };
    });
    setSnackbar({ open: true, message: "All chats marked as read", severity: "success" });
  };

  /* ───────────────────────── render ───────────────────────── */

  if (isLoadingCampaigns || isCampaignsError) {
    return (
      <Box sx={layoutCSS}>
        <Box sx={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2 }}>
          {isCampaignsError ? (
            <>
              <Typography variant="body1" color="text.secondary">Failed to load chats.</Typography>
              <Button variant="outlined" onClick={refetchCampaigns}>Try again</Button>
            </>
          ) : (
            <>
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">Please wait...</Typography>
            </>
          )}
        </Box>
      </Box>
    );
  }

  const isDirectChat = !!chatCampaign?.is_direct_message;
  const relatedName = chatCampaign?.related_campaign_id
    ? campaignById.get(String(chatCampaign.related_campaign_id))?.campaign_name || "a campaign"
    : null;
  const chatSubtitle = !selectedChat
    ? ""
    : isDirectChat
      ? relatedName ? `Direct chat · replied to ${relatedName}` : "Direct chat"
      : `Campaign · ${chatCampaign?.campaign_name || selectedChat.campaign_id}`;

  const sameChat = (campaign_id, number) =>
    selectedChat && String(selectedChat.campaign_id) === String(campaign_id) && selectedChat.number === number;

  return (
    <Box sx={layoutCSS}>
      <style>{"@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');"}</style>
      <Box sx={{ display: "flex", height: "100vh", bgcolor: "#f4f6fb", fontFamily: "'Inter', system-ui, sans-serif", "& *": { fontFamily: "inherit" } }}>

        {/* ═════════ Sidebar ═════════ */}
        <Box sx={{ width: 330, flexShrink: 0, display: "flex", flexDirection: "column", bgcolor: "#fff", borderRight: `1px solid ${LINE}` }}>
          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
              <Box sx={{ width: 34, height: 34, borderRadius: "11px", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`, boxShadow: "0 6px 14px rgba(99,102,241,.35)" }}>
                <ForumOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK, flex: 1, letterSpacing: "-0.01em" }}>Live Chat</Typography>
              <Tooltip title={totalUnread ? "Mark all as read" : "Everything is read"}>
                <span>
                  <IconButton size="small" onClick={handleMarkAllAsRead} disabled={totalUnread === 0} sx={{ color: "#64748b" }}>
                    <DoneAllIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh">
                <span>
                  <IconButton size="small" onClick={refetchCampaigns} disabled={isFetchingCampaigns} sx={{ color: "#64748b" }}>
                    {isFetchingCampaigns ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <TextField
              size="small"
              fullWidth
              placeholder="Search number or campaign"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}><CloseIcon sx={{ fontSize: 15 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.82rem", borderRadius: "11px", bgcolor: "#f4f6fb",
                  "& fieldset": { borderColor: "transparent" },
                  "&:hover fieldset": { borderColor: "#dbe0ec" },
                  "&.Mui-focused fieldset": { borderColor: BRAND, borderWidth: 1.5 },
                },
              }}
            />
          </Box>

          {/* two slim tabs; the one you click slides open from the top */}
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1, px: 1.5, pb: 1.5 }}>
            <SlideSection
              icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
              label="Direct messages"
              hint="People who wrote to you"
              count={directChats.length}
              unread={directUnread}
              open={openSection === "direct"}
              onToggle={() => toggleSection("direct")}
            >
              {directChats.length === 0 ? (
                <EmptyNote>{q ? "No direct chats match your search" : "No direct messages yet"}</EmptyNote>
              ) : (
                directChats.map((c) => (
                  <ChatRow
                    key={c.campaign_id}
                    selected={sameChat(c.campaign_id, c.number)}
                    onClick={() => openChat(c.campaign_id, c.number)}
                    avatar={<ContactAvatar seed={c.number || c.campaign_id} />}
                    title={c.title}
                    subtitle={c.subtitle}
                    time={shortTime(lastActivity(c))}
                    unread={c.unread}
                  />
                ))
              )}
            </SlideSection>

            <SlideSection
              icon={<CampaignIcon sx={{ fontSize: 17 }} />}
              label="Campaigns"
              hint="Replies to your broadcasts"
              count={campaignRows.length}
              unread={campaignsUnread}
              open={openSection === "campaigns"}
              onToggle={() => toggleSection("campaigns")}
            >
              {campaignRows.length === 0 ? (
                <EmptyNote>{q ? "No campaigns match your search" : "No campaigns found"}</EmptyNote>
              ) : (
                campaignRows.map((c) => {
                  const open = String(expandedId) === String(c.campaign_id);
                  return (
                    <Box key={c.campaign_id} sx={{ mb: 0.5 }}>
                      <ButtonBase
                        onClick={() => toggleCampaign(c.campaign_id)}
                        aria-expanded={open}
                        sx={{
                          width: "100%", display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1.1, textAlign: "left",
                          px: 1.25, py: 1, borderRadius: "10px", bgcolor: open ? "#f5f6ff" : "transparent",
                          transition: `background .15s ${EASE}`, "&:hover": { bgcolor: open ? "#f5f6ff" : "#f6f8fc" },
                        }}
                      >
                        <Box sx={{ width: 34, height: 34, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: c.unread ? "#eef2ff" : "#f1f5f9", color: c.unread ? BRAND : "#94a3b8" }}>
                          <CampaignIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: "0.83rem", fontWeight: c.unread ? 700 : 600, color: INK }}>
                            {c.campaign_name || `Campaign ${c.campaign_id}`}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>
                            {c.inbound_count || 0} replies · {c.total_contacts || 0} contacts
                          </Typography>
                        </Box>
                        <UnreadPill n={c.unread} />
                        <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8", transform: open ? "rotate(180deg)" : "none", transition: `transform .25s ${EASE}` }} />
                      </ButtonBase>

                      <Collapse in={open} timeout={260} unmountOnExit>
                        <Box sx={{ ml: 2.25, pl: 0.5, borderLeft: `2px solid ${LINE}`, mt: 0.25 }}>
                          {!listContacts ? (
                            <Box sx={{ p: 1.5, textAlign: "center" }}>
                              {listQ.isError
                                ? <Typography variant="caption" color="error">Could not load contacts</Typography>
                                : <CircularProgress size={16} />}
                            </Box>
                          ) : listContacts.length === 0 ? (
                            <EmptyNote>No replies yet</EmptyNote>
                          ) : (
                            listContacts.map((p) => (
                              <ChatRow
                                key={p.number}
                                selected={sameChat(c.campaign_id, p.number)}
                                onClick={() => openChat(c.campaign_id, p.number)}
                                avatar={<ContactAvatar seed={p.number} size={30} />}
                                title={formatPhone(p.number)}
                                subtitle={p.lastText}
                                time={shortTime(p.lastTs)}
                                unread={p.unread}
                              />
                            ))
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })
              )}

            </SlideSection>

            {pagination.total > 0 && (
              <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, bgcolor: "#fff", border: `1px solid ${LINE}`, borderRadius: "12px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, flexWrap: "wrap" }}>
                  {pagination.total_pages > 1 && (
                    <Pagination
                      count={pagination.total_pages} page={pagination.page} color="primary" size="small"
                      onChange={(_, value) => {
                        if (value === 1) setCampaignsList([]);
                        setPagination((prev) => ({ ...prev, page: value }));
                      }}
                      sx={{ "& .MuiPaginationItem-root": { fontSize: "0.7rem", minWidth: 24, height: 24 } }}
                    />
                  )}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem", whiteSpace: "nowrap" }}>
                      Rows per page
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 68 }}>
                      <Select
                        value={pagination.limit}
                        inputProps={{ "aria-label": "Rows per page" }}
                        onChange={(event) => {
                          const limit = Number(event.target.value);
                          setCampaignsList([]);
                          setPagination((prev) => ({ ...prev, page: 1, limit }));
                        }}
                        sx={{ height: 28, fontSize: "0.7rem" }}
                      >
                        {[5, 10, 15, 20].map((limit) => (
                          <MenuItem key={limit} value={limit} sx={{ fontSize: "0.7rem" }}>
                            {limit}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.65rem" }}>
                  Page {pagination.page} of {pagination.total_pages} · {pagination.total} total
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* ═════════ Main chat ═════════ */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
          {selectedChat ? (
            <>
              {/* header */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, minHeight: 66, flexShrink: 0, bgcolor: "#fff", borderBottom: `1px solid ${LINE}` }}>
                <ContactAvatar seed={selectedChat.number} size={42} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography noWrap sx={{ fontSize: "0.98rem", fontWeight: 700, color: INK, letterSpacing: "-0.01em" }}>
                    {formatPhone(selectedChat.number)}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>{chatSubtitle}</Typography>
                </Box>
                {lastInbound && (
                  <Chip
                    size="small"
                    icon={<LockClockIcon sx={{ fontSize: "14px !important" }} />}
                    label={sessionExpired ? "Session expired" : `Session ${sessionTimeRemaining}`}
                    sx={{
                      height: 26, fontSize: "0.7rem", fontWeight: 600,
                      bgcolor: sessionExpired ? "#fef2f2" : "#ecfdf5", color: sessionExpired ? "#b91c1c" : "#047857",
                      "& .MuiChip-icon": { color: "inherit" },
                    }}
                  />
                )}
                <Tooltip title="Refresh messages">
                  <span>
                    <IconButton size="small" onClick={() => chatQ.refetch()} disabled={chatQ.isFetching} sx={{ color: chatQ.isError ? "#ef4444" : "#64748b" }}>
                      <RefreshIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={showDetails ? "Hide details" : "Show details"}>
                  <IconButton size="small" onClick={() => setShowDetails((v) => !v)} sx={{ color: showDetails ? BRAND : "#64748b", bgcolor: showDetails ? "#eef2ff" : "transparent" }}>
                    <InfoOutlinedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* messages */}
              <Box
                sx={{
                  flex: 1, overflowY: "auto", px: { xs: 2, md: 4 }, py: 2,
                  background: "linear-gradient(180deg,#f6f7fc 0%,#eef1f9 100%)",
                  "&::-webkit-scrollbar": { width: 6 }, "&::-webkit-scrollbar-thumb": { bgcolor: "#cdd4e4", borderRadius: 3 },
                }}
              >
                {!chatReplies && !chatQ.isError ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", gap: 1.5 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading messages…</Typography>
                  </Box>
                ) : chatQ.isError && !chatReplies ? (
                  <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", gap: 1 }}>
                    <Typography variant="body2" color="error">Could not load messages.</Typography>
                    <Button size="small" onClick={() => chatQ.refetch()}>Try again</Button>
                  </Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Typography variant="body2" color="text.secondary">No messages in this chat.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, maxWidth: 860, mx: "auto" }}>
                    {messages.map((msg, idx) => {
                      const prev = idx > 0 ? messages[idx - 1] : null;
                      const showDateSep = !!msg.timestamp && (!prev || formatDateLabel(msg.timestamp) !== formatDateLabel(prev.timestamp));
                      const mine = msg.sender === "agent"; // our side on the right, customer on the left
                      const sameAsPrev = prev && prev.sender === msg.sender && !showDateSep;
                      return (
                        <React.Fragment key={msg.id}>
                          {showDateSep && (
                            <Box sx={{ display: "flex", justifyContent: "center", my: 1.25 }}>
                              <Chip
                                label={formatDateLabel(msg.timestamp)}
                                size="small"
                                sx={{ bgcolor: "#fff", color: "#64748b", fontSize: "0.68rem", fontWeight: 600, height: 24, border: `1px solid ${LINE}` }}
                              />
                            </Box>
                          )}
                          <Box sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", mt: sameAsPrev ? 0 : 0.6 }}>
                            <Box
                              sx={{
                                maxWidth: "72%", px: 1.75, pt: 1, pb: 0.7,
                                borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                                bgcolor: msg.error ? "#fef2f2" : mine ? undefined : "#fff",
                                background: msg.error ? undefined : mine ? `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})` : undefined,
                                color: msg.error ? "#b91c1c" : mine ? "#fff" : INK,
                                border: msg.error ? "1px solid #fecaca" : mine ? "none" : `1px solid ${LINE}`,
                                boxShadow: mine && !msg.error ? "0 4px 12px rgba(79,70,229,.22)" : "0 1px 2px rgba(15,23,42,.05)",
                                opacity: msg.isPending ? 0.65 : 1,
                                transition: "opacity .2s",
                              }}
                            >
                              <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{msg.text}</Typography>
                              <Typography sx={{ fontSize: "0.62rem", mt: 0.35, textAlign: "right", opacity: 0.75 }}>
                                {msg.time}
                                {msg.isPending && " · sending…"}
                                {msg.error && ` · not sent: ${msg.errorMessage || "error"}`}
                              </Typography>
                            </Box>
                          </Box>
                        </React.Fragment>
                      );
                    })}
                    <div ref={bottomRef} />
                  </Box>
                )}
              </Box>

              {/* composer */}
              <Box sx={{ px: { xs: 2, md: 4 }, py: 1.5, bgcolor: "#fff", borderTop: `1px solid ${LINE}`, flexShrink: 0 }}>
                <Box sx={{ maxWidth: 860, mx: "auto" }}>
                  {sessionExpired && (
                    <Alert
                      severity="warning"
                      icon={<LockClockIcon sx={{ fontSize: 17 }} />}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setTemplateOpen(true)}
                          sx={{ textTransform: "none", fontWeight: 700, fontSize: "0.76rem" }}
                        >
                          Initiate chat
                        </Button>
                      }
                      sx={{ mb: 1.25, py: 0.25, borderRadius: "12px", fontSize: "0.78rem", "& .MuiAlert-message": { fontSize: "0.78rem" } }}
                    >
                      The 24-hour reply window is over. Send an approved template to start the chat again.
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={5}
                      placeholder={sessionExpired ? "Waiting for the customer to message…" : "Write a message…  (Enter to send, Shift+Enter for a new line)"}
                      size="small"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                      }}
                      disabled={isSendingMessage || sessionExpired}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "16px", bgcolor: sessionExpired ? "#fffbeb" : "#f4f6fb", fontSize: "0.88rem", py: 1,
                          "& fieldset": { borderColor: "transparent" },
                          "&:hover fieldset": { borderColor: "#dbe0ec" },
                          "&.Mui-focused fieldset": { borderColor: BRAND, borderWidth: 1.5 },
                        },
                      }}
                    />
                    <IconButton
                      onClick={handleSend}
                      disabled={!input.trim() || isSendingMessage || sessionExpired}
                      sx={{
                        width: 44, height: 44, flexShrink: 0, color: "#fff", borderRadius: "14px",
                        background: `linear-gradient(135deg, ${BRAND}, ${BRAND_DARK})`, boxShadow: "0 6px 14px rgba(79,70,229,.3)",
                        "&:hover": { background: BRAND_DARK },
                        "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
                      }}
                    >
                      {isSendingMessage ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: 19 }} />}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1.25, textAlign: "center", px: 3 }}>
              <Box sx={{ width: 76, height: 76, borderRadius: "24px", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#eef2ff", color: BRAND }}>
                <ForumOutlinedIcon sx={{ fontSize: 38 }} />
              </Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK }}>Select a conversation</Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", maxWidth: 340 }}>
                Open <b>Direct messages</b> for people who wrote to you, or <b>Campaigns</b> to see replies to your broadcasts.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ═════════ Details ═════════ */}
        {selectedChat && showDetails && (
          <Box sx={{ width: 250, flexShrink: 0, bgcolor: "#fff", borderLeft: `1px solid ${LINE}`, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <Box sx={{ px: 2, py: 2.5, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.75 }}>
              <ContactAvatar seed={selectedChat.number} size={64} />
              <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: INK }}>{formatPhone(selectedChat.number)}</Typography>
              <Chip
                label={isDirectChat ? "Direct chat" : "Campaign reply"}
                size="small"
                sx={{ height: 22, fontSize: "0.68rem", fontWeight: 600, bgcolor: isDirectChat ? "#eef2ff" : "#fdf2f8", color: isDirectChat ? BRAND_DARK : "#be185d" }}
              />
            </Box>
            <Divider />

            <Box sx={{ px: 2, py: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <InfoRow label={isDirectChat ? "Started from" : "Campaign"}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", wordBreak: "break-word" }}>
                  {isDirectChat ? (relatedName ? `Reply to ${relatedName}` : "Direct message") : (chatCampaign?.campaign_name || selectedChat.campaign_id)}
                </Typography>
              </InfoRow>
              <InfoRow label="Messages">
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155" }}>{serverMessages.length}</Typography>
              </InfoRow>
              <InfoRow label="24-hour window">
                {lastInbound ? (
                  <Chip
                    label={sessionExpired ? "Expired" : sessionTimeRemaining}
                    size="small"
                    sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: sessionExpired ? "#fef2f2" : "#ecfdf5", color: sessionExpired ? "#b91c1c" : "#047857" }}
                  />
                ) : <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>—</Typography>}
              </InfoRow>
            </Box>
            <Divider />

            <Box sx={{ px: 2, py: 2 }}>
              <InfoRow label="Assign to agent">
                <FormControl fullWidth size="small">
                  <Select
                    value={selectedAgentId}
                    displayEmpty
                    onChange={(e) => handleAssignAgent(e.target.value)}
                    renderValue={(v) => {
                      const a = (agentsResponse || []).find((x) => x.agent_id === v);
                      return a ? a.name : <span style={{ color: "#94a3b8" }}>Not assigned</span>;
                    }}
                    sx={{ fontSize: "0.8rem", borderRadius: "10px", bgcolor: "#f4f6fb", "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#dbe0ec" } }}
                  >
                    {(agentsResponse || []).map((agent) => (
                      <MenuItem key={agent.agent_id} value={agent.agent_id} sx={{ fontSize: "0.8rem" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.9 }}>
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: agent.is_active ? "#22c55e" : "#ef4444", flexShrink: 0 }} />
                          {agent.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </InfoRow>
            </Box>
            <Divider />

            <Box sx={{ px: 2, py: 2 }}>
              <Button
                variant="outlined" color="error" fullWidth size="small"
                startIcon={<BlockIcon sx={{ fontSize: "15px!important" }} />}
                sx={{ borderRadius: "10px", fontSize: "0.76rem", py: 0.7, textTransform: "none", fontWeight: 600 }}
              >
                Block customer
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ═════════ Initiate chat (send approved template) ═════════ */}
      <Dialog open={templateOpen} onClose={closeTemplateDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 700 }}>
          Initiate chat · {selectedChat ? formatPhone(selectedChat.number) : ""}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            fullWidth
            placeholder="Search template"
            value={tplSearch}
            onChange={(e) => setTplSearch(e.target.value)}
            sx={{ mb: 1.5 }}
          />

          {isLoadingTemplates || !chatWabaId ? (
            <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={22} /></Box>
          ) : approvedTemplates.length === 0 ? (
            <EmptyNote>No approved templates found</EmptyNote>
          ) : (
            <Box sx={{ maxHeight: 220, overflowY: "auto", border: `1px solid ${LINE}`, borderRadius: "10px" }}>
              {approvedTemplates.map((t) => (
                <ButtonBase
                  key={t.template_id}
                  onClick={() => { setTplSelectedId(t.template_id); setTplValues({}); }}
                  sx={{
                    width: "100%", justifyContent: "flex-start", gap: 1, px: 1.25, py: 0.75, textAlign: "left",
                    bgcolor: tplSelectedId === t.template_id ? "#eef2ff" : "transparent",
                    "&:hover": { bgcolor: "#f6f8fc" },
                  }}
                >
                  <Radio size="small" checked={tplSelectedId === t.template_id} sx={{ p: 0.5 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{t.name}</Typography>
                    <Typography noWrap sx={{ fontSize: "0.7rem", color: "#94a3b8" }}>{t.category}</Typography>
                  </Box>
                </ButtonBase>
              ))}
            </Box>
          )}

          {selectedTpl && (
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
              {tplFields.map((f) => (
                <TextField
                  key={f.key}
                  size="small"
                  fullWidth
                  label={f.label}
                  value={tplValues[f.key] || ""}
                  onChange={(e) => setTplValues((v) => ({ ...v, [f.key]: e.target.value }))}
                />
              ))}
              <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: "#DCF8C6", fontSize: "0.82rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {renderBody(selectedTpl, tplFields, tplValues) || selectedTpl.name}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTemplateDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendTemplate}
            disabled={!selectedTpl || !tplFilled || isSendingMessage}
            startIcon={isSendingMessage ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
          >
            {isSendingMessage ? "Sending..." : "Send template"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%", fontSize: "0.8rem" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}